import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaEye, FaMoneyBill, FaBan, FaPrint, FaFileExcel, FaEdit, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getNotesPayable, clearError, clearSuccess, getBanks, payNotePayable, cancelNotePayable } from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

const NotesPayable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { notesPayablePagination, error, success, banks, banksPagination } = useSelector((s) => s.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [payId, setPayId] = useState(null);
  const [payForm, setPayForm] = useState({ bank_account_id: '', amount: '', payment_date: '' });
  const [cancelId, setCancelId] = useState(null);
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const [bankCurrentPage, setBankCurrentPage] = useState(1);
  const bankSearchInputRef = useRef(null);
  const bankSearchTimeoutRef = useRef(null);
  const [noteForPrint, setNoteForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [previewNote, setPreviewNote] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [allNotesPayable, setAllNotesPayable] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('sidenav.notesPayable22');
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [setTitle, dispatch]);

  // Fetch all notes payable on load with per_page = total
  const fetchNotesPayable = async () => {
    try {
      // First fetch to get total count
      const params = {
        page: 1,
        per_page: perPage
      };
      if (searchTerm) {
        params.search = searchTerm;
      }

      const result = await dispatch(getNotesPayable(params));
      if (getNotesPayable.fulfilled && result.type === getNotesPayable.fulfilled.type) {
        const response = result.payload;
        const data = response?.data || response;
        const paginationData = data?.meta || data?.pagination || notesPayablePagination || null;
        const total = paginationData?.total || (Array.isArray(data?.data) ? data.data.length : (Array.isArray(data) ? data.length : 0));

        if (total > 0) {
          // Fetch all notes
          const allParams = {
            page: 1,
            per_page: total
          };
          if (searchTerm) {
            allParams.search = searchTerm;
          }

          const allResult = await dispatch(getNotesPayable(allParams));
          if (getNotesPayable.fulfilled && allResult.type === getNotesPayable.fulfilled.type) {
            const allResponse = allResult.payload;
            const allData = allResponse?.data || allResponse;
            let notesList = [];
            if (Array.isArray(allData?.data)) {
              notesList = allData.data;
            } else if (Array.isArray(allData)) {
              notesList = allData;
            }
            setAllNotesPayable(notesList);
            setPagination(paginationData);
          }
        } else {
          setAllNotesPayable([]);
          setPagination(null);
        }
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchNotesPayable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, i18n.language, searchTerm]);

  // Load banks for pay modal with pagination
  useEffect(() => {
    if (bankDropdownOpen) {
      dispatch(getBanks({ page: bankCurrentPage, per_page: 10, search: bankSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCurrentPage, bankDropdownOpen, bankSearchTerm]);

  // Search banks with debounce
  useEffect(() => {
    if (bankSearchTerm !== undefined && bankDropdownOpen) {
      if (bankSearchTimeoutRef.current) clearTimeout(bankSearchTimeoutRef.current);
      bankSearchTimeoutRef.current = setTimeout(() => {
        setBankCurrentPage(1);
        dispatch(getBanks({ page: 1, per_page: 10, search: bankSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (bankSearchTimeoutRef.current) clearTimeout(bankSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankSearchTerm, bankDropdownOpen]);

  useEffect(() => {
    if (success) {
      toast.success(success, {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      dispatch(clearSuccess());
      fetchNotesPayable();
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  // Local pagination
  const filteredNotesPayable = allNotesPayable || [];

  const SORT_FIELDS = { VENDOR: 'vendor_name', NOTE_NUMBER: 'note_number', AMOUNT: 'amount', ISSUE_DATE: 'issue_date', DUE_DATE: 'due_date', STATUS: 'status' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  const sortedNotes = useMemo(() => {
    if (!sortField) return filteredNotesPayable;
    return [...filteredNotesPayable].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
      if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredNotesPayable, sortField, sortOrder]);

  const currentPageSafe = Math.max(1, currentPage);
  const totalPages = Math.ceil(sortedNotes.length / perPage);
  const startIndex = (currentPageSafe - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedNotesPayable = sortedNotes.slice(startIndex, endIndex);
  const items = paginatedNotesPayable;

  const buildDynamicPdfChunks = (notes) => {
    const topHeader = document.getElementById('notes-payable-pdf-measure-top-header');
    const thead = document.getElementById('notes-payable-pdf-measure-thead');
    const footer = document.getElementById('notes-payable-pdf-measure-footer');
    const tbody = document.getElementById('notes-payable-pdf-measure-tbody');
    return calculatePdfPagination(notes, { topHeader, thead, footer, tbody });
  };

  // Use banks directly from API (search is handled server-side)
  const displayBanks = Array.isArray(banks) ? banks : [];
  const bankPagination = banksPagination || { current_page: 1, last_page: 1 };

  useEffect(() => {
    if (bankDropdownOpen && bankSearchInputRef.current) {
      bankSearchInputRef.current.focus();
    }
  }, [bankDropdownOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return convertArabicNumbersToEnglish(date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }));
    } catch {
      return convertArabicNumbersToEnglish(dateString?.split('T')[0] || dateString || '—');
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || amount === '') return '—';
    const num = Number(amount);
    // Always convert to English, even if input is Arabic or text
    if (isNaN(num)) return convertArabicNumbersToEnglish(String(amount));
    return convertArabicNumbersToEnglish(num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  const getStatusText = (status) => {
    const s = String(status || '').toLowerCase();
    const map = {
      paid: t('notesPayable.status.paid'),
      under_payment: t('notesPayable.status.under_payment'),
      canceled: t('notesPayable.status.canceled'),
      unpaid: t('notesPayable.status.unpaid'),
    };
    return map[s] || status || '—';
  };

  const handleApplyPerPage = () => {
    const total = pagination?.total ?? allNotesPayable?.length ?? 1000;
    const maxAllowed = Number.isFinite(total) && total > 0 ? total : 1000;
    const next = Math.min(Math.max(1, Number(perPageInput) || 1), maxAllowed);
    setPerPage(next);
    setPerPageInput(next);
    setCurrentPage(1);
  };

  // Handle preview note payable PDF
  const handlePreviewNote = (note) => {
    setPreviewNote(note);
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  // Handle preview all notes payable
  const handlePreviewAll = () => {
    if (!sortedNotes || sortedNotes.length === 0) {
      toast.error(t('notesPayable.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }
    const chunks = buildDynamicPdfChunks(sortedNotes);
    setPdfChunks(chunks);
    setPreviewNote({ all: true, notes: sortedNotes });
    setIsPreviewOpen(true);
  };

  // Handle print from preview
  const handlePrintFromPreview = async () => {
    if (!previewNote) return;
    setIsPreviewOpen(false);
    if (previewNote.all) {
      await handlePrintAll();
    } else {
      await handlePrintNotePayable(previewNote);
    }
  };

  // Handle print single note payable
  const handlePrintNotePayable = async (note) => {
    try {
      setNoteForPrint(note);
      setPdfType('single');
      await waitForRender();
      setIsGeneratingPdf(true);

      // Shared PDF Engine — capture + assembly
      await generateSingleElementPdf({
        elementId: `note-payable-pdf-${note.id}`,
        fileName: datedFileName(`ACE_NotePayable_${note.note_number || note.id}`),
        isRTL,
      });
    } catch (err) {
      toast.error(t('notesPayable.print.error'), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setNoteForPrint(null);
      setPdfType(null);
    }
  };

  // Handle print all notes payable
  const handlePrintAll = async () => {
    if (!sortedNotes || sortedNotes.length === 0) {
      toast.error(t('notesPayable.print.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      const chunks = buildDynamicPdfChunks(sortedNotes);
      setPdfType('all');
      setPdfChunks(chunks);

      await waitForRender();

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      await generatePdfFromElementIds({
        elementIds: chunks.map((_, i) => `notes-payable-pdf-all-${i}`),
        fileName: datedFileName(t('notesPayable.excel.fileName')),
        isRTL,
      });
      toast.success(t('notesPayable.print.success'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('notesPayable.print.error'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
    }
  };

  // Handle export single note payable to Excel
  const handleExportNotePayableToExcel = (note) => {
    const data = [{
      [t('notesPayable.tableHeaders.vendor', { defaultValue: 'المورد' })]: note.vendor_name || note.vendor?.name_ar || note.vendor?.name || '—',
      [t('notesPayable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })]: note.note_number || '—',
      [t('notesPayable.tableHeaders.amount', { defaultValue: 'القيمة' })]: formatCurrency(note.amount) || '—',
      [t('notesPayable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })]: formatDate(note.issue_date) || '—',
      [t('notesPayable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })]: formatDate(note.due_date) || '—',
      [t('notesPayable.tableHeaders.status', { defaultValue: 'الحالة' })]: getStatusText(note.status) || '—'
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('notesPayable.notePayable', { defaultValue: 'Note Payable' }));
    const identifier = note.note_number || note.id;
    const fileName = `${isRTL ? 'ورقة_دفع' : 'NotePayable'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('notesPayable.export.success'), { rtl: isRTL });
  };

  // Handle export all notes payable to Excel
  const handleExportAllToExcel = () => {
    if (!sortedNotes || sortedNotes.length === 0) {
      toast.error(t('notesPayable.export.noData'), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    const data = sortedNotes.map((note, index) => ({
      [t('notesReceivable.tableHeaders.serialNumber')]: index + 1,
      [t('notesPayable.tableHeaders.vendor')]: note.vendor_name || (isRTL ? (note.vendor?.name_ar || note.vendor?.name) : (note.vendor?.name_en || note.vendor?.name_ar || note.vendor?.name)) || '—',
      [t('notesPayable.tableHeaders.noteNumber')]: note.note_number || '—',
      [t('notesPayable.tableHeaders.amount')]: formatCurrency(note.amount) || '—',
      [t('notesPayable.tableHeaders.issueDate')]: formatDate(note.issue_date) || '—',
      [t('notesPayable.tableHeaders.dueDate')]: formatDate(note.due_date) || '—',
      [t('notesPayable.tableHeaders.status')]: getStatusText(note.status) || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('notesPayable.excel.sheetName'));
    const fileName = `${t('notesPayable.excel.fileName')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('notesPayable.export.success'), {
      rtl: isRTL,
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };

  return (
    <>
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
        <h1 style={{ margin: '10px 0', fontSize: '20px', textAlign }}>{t('notesPayable.title')}</h1>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
                {t('common.perPage', { defaultValue: 'Per Page' })}:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => {
                    const newValue = Math.max(1, perPageInput - 1);
                    setPerPageInput(newValue);
                  }}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '6px 10px',
                    borderRadius: '6px 0 0 6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '32px'
                  }}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max={pagination?.total ?? allNotesPayable?.length ?? 1000}
                  value={perPageInput}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 1;
                    const maxValue = pagination?.total ?? allNotesPayable?.length ?? 1000;
                    setPerPageInput(Math.max(1, Math.min(maxValue, value)));
                  }}
                  style={{
                    width: '70px',
                    padding: '6px 10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderRadius: 0,
                    color: 'white',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={() => {
                    const maxValue = pagination?.total ?? allNotesPayable?.length ?? 1000;
                    const newValue = Math.min(maxValue, perPageInput + 1);
                    setPerPageInput(newValue);
                  }}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '6px 10px',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '32px'
                  }}
                >
                  +
                </button>
              </div>
              <button
                onClick={handleApplyPerPage}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('common.apply', { defaultValue: 'تطبيق' })}
              </button>
            </div>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <FaEye /> {t('notesPayable.preview.all')}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf || !sortedNotes || sortedNotes.length === 0}
              style={{
                backgroundColor: isGeneratingPdf || !sortedNotes || sortedNotes.length === 0 ? '#666' : '#1f5ff1',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                cursor: isGeneratingPdf || !filteredNotesPayable || filteredNotesPayable.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexDirection: isRTL ? 'row' : 'row-reverse',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('notesPayable.print.generating') : t('notesPayable.print.all')}
            </button>
            <button
              onClick={handleExportAllToExcel}
              disabled={!sortedNotes || sortedNotes.length === 0}
              style={{
                backgroundColor: !sortedNotes || sortedNotes.length === 0 ? '#666' : '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                cursor: !filteredNotesPayable || filteredNotesPayable.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaFileExcel /> {t('notesPayable.export.all')}
            </button>
            <button
              onClick={() => navigate('/notes-payable/add')}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaPlus /> {t('notesPayable.add')}
            </button>
          </div>
        </div>

        <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 12, marginBottom: 16, direction: dir }}>
          <input
            type="text"
            placeholder={t('notesPayable.search.placeholder')}
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
              searchTimeoutRef.current = setTimeout(() => {
                setCurrentPage(1);
              }, 300);
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: 8,
              color: 'white',
              fontSize: 14,
              direction: dir,
              textAlign
            }}
          />
        </div>

        <div style={{ overflowX: 'auto', background: '#1b3350', border: '1px solid #333', borderRadius: 12, direction: dir }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px', tableLayout: 'auto', direction: dir }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>#</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.VENDOR)}>{t('notesPayable.tableHeaders.vendor')}{getSortIcon(SORT_FIELDS.VENDOR)}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesPayable.project', { defaultValue: 'Project' })}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.NOTE_NUMBER)}>{t('notesPayable.tableHeaders.noteNumber')}{getSortIcon(SORT_FIELDS.NOTE_NUMBER)}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.AMOUNT)}>{t('notesPayable.tableHeaders.amount')}{getSortIcon(SORT_FIELDS.AMOUNT)}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.ISSUE_DATE)}>{t('notesPayable.tableHeaders.issueDate')}{getSortIcon(SORT_FIELDS.ISSUE_DATE)}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.DUE_DATE)}>{t('notesPayable.tableHeaders.dueDate')}{getSortIcon(SORT_FIELDS.DUE_DATE)}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.STATUS)}>{t('notesPayable.tableHeaders.status')}{getSortIcon(SORT_FIELDS.STATUS)}</th>
                <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesPayable.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: 20, direction: dir }}>{t('notesPayable.noData')}</td>
                </tr>
              )}
              {items.map((np, index) => (
                <tr key={np.id}>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>{startIndex + index + 1}</td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap', direction: dir }}>
                    {np.vendor_name || (isRTL ? (np.vendor?.name_ar || np.vendor?.name) : (np.vendor?.name_en || np.vendor?.name_ar || np.vendor?.name)) || '—'}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap', direction: dir }}>
                    {np.project_name || (isRTL ? (np.project?.name_ar || np.project?.name) : (np.project?.name_en || np.project?.name_ar || np.project?.name)) || '—'}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {np.note_number || '—'}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {np.amount != null ? convertArabicNumbersToEnglish(Number(np.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : '—'}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {formatDate(np.issue_date)}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {formatDate(np.due_date)}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center' }}>
                    {(() => {
                      const s = String(np.status || '').toLowerCase();
                      const bgMap = {
                        paid: '#198754',
                        unpaid: '#ffc107',
                        canceled: '#dc3545',
                      };
                      const bg = bgMap[s] || '#6c757d';
                      const color = s === 'unpaid' ? '#000' : 'white';
                      return (
                        <span style={{ backgroundColor: bg, color: color, padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 'bold', display: 'inline-block', whiteSpace: 'nowrap', direction: dir }}>
                          {getStatusText(np.status)}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'nowrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <button
                        onClick={() => handlePreviewNote(np)}
                        style={{ background: '#444', color: 'white', border: 'none', borderRadius: 6, padding: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        title={t('notesPayable.actions.view', { defaultValue: 'عرض' })}
                      >
                        <FaEye />
                      </button>
                      {String(np.status).toLowerCase() === 'unpaid' && (
                        <button
                          onClick={() => navigate(`/notes-payable/edit/${np.id}`)}
                          style={{ background: '#ffc107', color: '#000', border: 'none', borderRadius: 6, padding: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                          title={t('common.edit', { defaultValue: 'تعديل' })}
                        >
                          <FaEdit />
                        </button>
                      )}
                      <button
                        onClick={() => handlePrintNotePayable(np)}
                        disabled={isGeneratingPdf}
                        style={{ background: isGeneratingPdf ? '#666' : '#1f5ff1', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: isGeneratingPdf ? 0.6 : 1 }}
                        title={t('notesPayable.actions.print')}
                      >
                        <FaPrint />
                      </button>
                      <button
                        onClick={() => handleExportNotePayableToExcel(np)}
                        style={{ background: '#27ae60', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        title={t('notesPayable.actions.export')}
                      >
                        <FaFileExcel />
                      </button>
                      {(() => {
                        const status = String(np.status || '').toLowerCase();
                        const isPaid = status === 'paid';
                        const isCanceled = status === 'canceled';
                        const showActions = !isPaid && !isCanceled;
                        return (
                          <>
                            {showActions && (
                              <button
                                onClick={() => { setPayId(np.id); setPayForm({ bank_account_id: '', amount: np?.amount ?? '', payment_date: '' }); }}
                                style={{ background: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                title={t('notesPayable.actions.pay')}
                              >
                                <FaMoneyBill />
                              </button>
                            )}
                            {showActions && (
                              <button
                                onClick={() => setCancelId(np.id)}
                                style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                title={t('notesPayable.actions.cancel')}
                              >
                                <FaBan />
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
            <button
              onClick={() => setCurrentPage(currentPageSafe - 1)}
              disabled={currentPageSafe === 1}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              {t('notesPayable.pagination.previous')}
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
              let page;
              if (totalPages <= 10) {
                page = i + 1;
              } else if (currentPageSafe <= 5) {
                page = i + 1;
              } else if (currentPageSafe >= totalPages - 4) {
                page = totalPages - 9 + i;
              } else {
                page = currentPageSafe - 4 + i;
              }
              return page;
            }).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: page === currentPageSafe ? '#AC2000' : '#1a1f2e',
                  color: 'white',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(currentPageSafe + 1)}
              disabled={currentPageSafe === totalPages}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              {t('notesPayable.pagination.next')}
            </button>
            <span style={{ color: '#9ca3af', fontSize: '14px', marginLeft: '10px', alignSelf: 'center' }}>
              {currentPageSafe} / {totalPages}
            </span>
          </div>
        )}

        {payId && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 20, width: 420, maxWidth: '95%' }}>
              <h3 style={{ color: 'white', margin: '0 0 12px', fontSize: '18px', textAlign }}>{t('notesPayable.payModal.title')}</h3>
              <div style={{ marginBottom: 12, position: 'relative' }}>
                <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 6, textAlign }}>{t('notesPayable.payModal.bankAccount')}</label>
                <div
                  onClick={() => {
                    setBankDropdownOpen((p) => !p);
                    if (bankDropdownOpen) {
                      setBankSearchTerm('');
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#1a1f2e',
                    color: 'white',
                    border: '1px solid #333',
                    borderRadius: 6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span>
                    {payForm.bank_account_id
                      ? (() => {
                        const b = (banks || []).find((x) => String(x.id) === String(payForm.bank_account_id));
                        return b ? `${b.code ? `${b.code} - ` : ''}${isRTL ? (b.name_ar || b.name || '') : (b.name_en || b.name_ar || b.name || '')}` : t('notesPayable.payModal.selectBankAccount');
                      })()
                      : t('notesPayable.payModal.selectBankAccount')}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                {bankDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 10,
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: 6,
                      marginTop: 4,
                      maxHeight: 300,
                      overflowY: 'auto',
                      paddingBottom: 4,
                    }}
                  >
                    <div style={{ padding: 8 }}>
                      <input
                        ref={bankSearchInputRef}
                        type="text"
                        placeholder={t('notesPayable.payModal.searchBankAccount')}
                        value={bankSearchTerm}
                        onChange={(e) => setBankSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: 8,
                          backgroundColor: '#14263b',
                          border: '1px solid #444',
                          borderRadius: 6,
                          color: 'white',
                          fontSize: 14,
                        }}
                      />
                    </div>
                    {bankPagination && bankPagination.last_page >= 1 && (
                      <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign }}>{t('notesPayable.payModal.page')} {bankPagination.current_page} {t('notesPayable.payModal.of')} {bankPagination.last_page}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (bankPagination.current_page > 1) {
                                setBankCurrentPage(1);
                              }
                            }}
                            disabled={bankPagination.current_page === 1}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: bankPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                              color: bankPagination.current_page === 1 ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: bankPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.payModal.firstPage')}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (bankPagination.current_page > 1) {
                                setBankCurrentPage(bankPagination.current_page - 1);
                              }
                            }}
                            disabled={bankPagination.current_page === 1}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: bankPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                              color: bankPagination.current_page === 1 ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: bankPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.payModal.previous')}
                          >
                            ‹
                          </button>
                          {(() => {
                            const pages = [];
                            const current = bankPagination.current_page;
                            const last = bankPagination.last_page;
                            const showPages = 5;
                            let start = Math.max(1, current - Math.floor(showPages / 2));
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1) {
                              start = Math.max(1, end - showPages + 1);
                            }
                            for (let i = start; i <= end; i++) {
                              pages.push(i);
                            }
                            return pages.map((page) => (
                              <button
                                key={page}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBankCurrentPage(page);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                  color: 'white',
                                  border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: 10,
                                  fontWeight: page === current ? 'bold' : 'normal',
                                  minWidth: '28px',
                                }}
                              >
                                {page}
                              </button>
                            ));
                          })()}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (bankPagination.current_page < bankPagination.last_page) {
                                setBankCurrentPage(bankPagination.current_page + 1);
                              }
                            }}
                            disabled={bankPagination.current_page === bankPagination.last_page}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: bankPagination.current_page === bankPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                              color: bankPagination.current_page === bankPagination.last_page ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: bankPagination.current_page === bankPagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.payModal.next')}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (bankPagination.current_page < bankPagination.last_page) {
                                setBankCurrentPage(bankPagination.last_page);
                              }
                            }}
                            disabled={bankPagination.current_page === bankPagination.last_page}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: bankPagination.current_page === bankPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                              color: bankPagination.current_page === bankPagination.last_page ? '#666' : 'white',
                              border: '1px solid #444',
                              borderRadius: '4px',
                              cursor: bankPagination.current_page === bankPagination.last_page ? 'not-allowed' : 'pointer',
                              fontSize: 10,
                              fontWeight: 'bold',
                              minWidth: '28px',
                            }}
                            title={t('notesPayable.payModal.lastPage')}
                          >
                            »»
                          </button>
                        </div>
                      </div>
                    )}
                    <div>
                      {displayBanks.map((b) => (
                        <div
                          key={b.id}
                          onClick={() => {
                            setPayForm({ ...payForm, bank_account_id: b.id });
                            setBankDropdownOpen(false);
                            setBankSearchTerm('');
                          }}
                          style={{
                            padding: 10,
                            cursor: 'pointer',
                            borderTop: '1px solid #333',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span>{`${b.code ? `${b.code} - ` : ''}${isRTL ? (b.name_ar || b.name || '') : (b.name_en || b.name_ar || b.name || '')}`}</span>
                        </div>
                      ))}
                      {displayBanks.length === 0 && (
                        <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>{t('notesPayable.payModal.noResults')}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 6, textAlign }}>{t('notesPayable.payModal.paymentAmount')}</label>
                  <input
                    type="number"

                    value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    style={{ width: '100%', padding: '10px', background: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 6, direction: dir, textAlign }}
                  />
                </div>
                <div>
                  <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 6, textAlign }}>{t('notesPayable.payModal.paymentDate')}</label>
                  <input
                    type="date"
                    value={payForm.payment_date}
                    onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                    style={{ width: '100%', padding: '10px', background: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 6, direction: dir, textAlign }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button onClick={() => setPayId(null)} style={{ background: '#666', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign }}>{t('notesPayable.payModal.cancel')}</button>
                <button
                  onClick={async () => {
                    if (!payForm.bank_account_id || !payForm.amount || !payForm.payment_date) {
                      toast.error(t('notesPayable.payModal.requiredFields'), { rtl: isRTL }); return;
                    }
                    try {
                      await dispatch(payNotePayable({ id: payId, data: { bank_account_id: Number(payForm.bank_account_id), amount: Number(payForm.amount), payment_date: payForm.payment_date } })).unwrap();
                      dispatch(clearSuccess());
                      setPayId(null);
                      setPayForm({ bank_account_id: '', amount: '', payment_date: '' });
                      dispatch(getNotesPayable({ page: currentPage, per_page: 10, search: searchTerm || undefined }));
                    } catch (error) {
                      dispatch(clearError());
                    }
                  }}
                  style={{ background: '#AC2000', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign }}
                >
                  {t('notesPayable.payModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {cancelId && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 20, width: 380, maxWidth: '95%' }}>
              <h3 style={{ color: 'white', margin: '0 0 12px', fontSize: '18px', textAlign }}>{t('notesPayable.cancelModal.title')}</h3>
              <p style={{ color: '#ccc', textAlign }}>{t('notesPayable.cancelModal.message')}</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button onClick={() => setCancelId(null)} style={{ background: '#666', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign }}>{t('notesPayable.cancelModal.cancel')}</button>
                <button
                  onClick={async () => {
                    try {
                      await dispatch(cancelNotePayable(cancelId)).unwrap();
                      dispatch(clearSuccess());
                      setCancelId(null);
                      dispatch(getNotesPayable({ page: currentPage, per_page: 10, search: searchTerm || undefined }));
                    } catch (error) {
                      dispatch(clearError());
                    }
                  }}
                  style={{ background: '#dc3545', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign }}
                >
                  {t('notesPayable.cancelModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Templates */}
        {noteForPrint && pdfType === 'single' && (
          <div
            id={`note-payable-pdf-${noteForPrint.id}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              minHeight: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                direction: isRTL ? 'rtl' : 'ltr',
                backgroundColor: '#ffffff',
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '95%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0,
                      display: 'block',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }}
                    crossOrigin="anonymous"
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.vendor')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.noteNumber')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.amount')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.issueDate')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.dueDate')}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{noteForPrint.vendor_name || (isRTL ? (noteForPrint.vendor?.name_ar || noteForPrint.vendor?.name) : (noteForPrint.vendor?.name_en || noteForPrint.vendor?.name_ar || noteForPrint.vendor?.name)) || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{noteForPrint.project_name || (isRTL ? (noteForPrint.project?.name_ar || noteForPrint.project?.name) : (noteForPrint.project?.name_en || noteForPrint.project?.name_ar || noteForPrint.project?.name)) || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{noteForPrint.note_number || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(noteForPrint.amount) || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(noteForPrint.issue_date) || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(noteForPrint.due_date) || '—'}</td>
                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{getStatusText(noteForPrint.status) || '—'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderTop: '4px double #000',
                        paddingTop: '8px',
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Print Templates for All Notes Payable - Multiple pages */}
        {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIndex) => (
          <AllNotesPayablePrintTemplate
            key={chunkIndex}
            chunkIndex={chunkIndex}
            notesPayable={chunk.items || chunk}
            startIndex={chunk.startIndex ?? chunkIndex * 25}
            pagination={null}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            getStatusText={getStatusText}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            totalChunks={pdfChunks.length}
          />
        ))}

        {/* PDF Preview Modal */}
        <PdfPreviewModal
          isOpen={isPreviewOpen && previewNote}
          title={previewNote?.all ? t('notesPayable.title') : t('notesPayable.preview.title')}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewNote(null);
            setPdfChunks([]);
          }}
          onPrint={handlePrintFromPreview}
          isGeneratingPdf={isGeneratingPdf}
          pdfChunks={pdfChunks}
          pdfType={previewNote?.all ? 'all' : 'single'}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px' }}>
            {previewNote?.all ? (
              (pdfChunks || []).map((chunk, chunkIndex) => (
                <AllNotesPayablePreviewTemplate
                  key={chunkIndex}
                  chunkIndex={chunkIndex}
                  notesPayable={chunk.items || chunk}
                  startIndex={chunk.startIndex ?? chunkIndex * 25}
                  pagination={null}
                  isRTL={isRTL}
                  t={t}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  getStatusText={getStatusText}
                  publicUrl={publicUrl}
                  headerLogo={headerLogo}
                  totalChunks={pdfChunks?.length || 1}
                />
              ))
            ) : (
              <NotePayablePreviewTemplate
                note={previewNote}
                isRTL={isRTL}
                t={t}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusText={getStatusText}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
              />
            )}
          </div>
        </PdfPreviewModal>

        {/* Measurement template for dynamic PDF pagination */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
          <div id="notes-payable-pdf-measure-top-header" style={{ width: '100%' }}>
            <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '95%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
            <h1 style={{ textAlign: 'center', fontSize: '18px' }}>{t('notesPayable.title')}</h1>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000' }}>
            <thead id="notes-payable-pdf-measure-thead">
              <tr>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>#</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.vendor')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.noteNumber')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.amount')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.issueDate')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.dueDate')}</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesPayable.tableHeaders.status')}</th>
              </tr>
            </thead>
            <tbody id="notes-payable-pdf-measure-tbody">
              {sortedNotes.slice(0, 5).map((note, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.vendor_name || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.project_name || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.note_number || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.amount || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.issue_date || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.due_date || '—'}</td>
                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.status || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div id="notes-payable-pdf-measure-footer" style={{ height: '120px' }} />
        </div>
      </div>
    </>
  );
};

// Print Template for All Notes Payable
const AllNotesPayablePrintTemplate = ({ notesPayable, pagination, isRTL, t, formatDate, formatCurrency, getStatusText, publicUrl, headerLogo, chunkIndex = 0, startIndex = 0, totalChunks = 1 }) => {
  if (!notesPayable || notesPayable.length === 0) return null;
  return (
    <div
      id={`notes-payable-pdf-all-${chunkIndex}`}
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        height: '1043px',
      }}>
        <div style={{
          border: '2px solid #000',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '95%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0,
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <h1 style={{ textAlign: "center", fontFamily: '"Times New Roman", "Times", serif', fontSize: '18px' }}>{t('notesPayable.title')}</h1>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>#</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.vendor')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.noteNumber')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.amount')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.issueDate')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.dueDate')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notesPayable.map((note, index) => {
                      const globalIndex = startIndex + index;
                      return (
                        <tr key={note.id || index} style={{ backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{globalIndex + 1}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{note.vendor_name || (isRTL ? (note.vendor?.name_ar || note.vendor?.name) : (note.vendor?.name_en || note.vendor?.name_ar || note.vendor?.name)) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{note.project_name || (isRTL ? (note.project?.name_ar || note.project?.name) : (note.project?.name_en || note.project?.name_ar || note.project?.name)) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{convertArabicNumbersToEnglish(note.note_number) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrency(note.amount) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDate(note.issue_date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDate(note.due_date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{getStatusText(note.status) || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px'
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Template Component
const NotePayablePreviewTemplate = ({ note, isRTL, t, formatDate, formatCurrency, getStatusText, publicUrl, headerLogo }) => {
  if (!note) return null;

  return (
    <div
      style={{
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        margin: '0 auto',
        overflow: 'hidden'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        height: '1043px',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '95%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0,
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.vendor')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.noteNumber')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.amount')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.issueDate')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.dueDate')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.vendor_name || (isRTL ? (note.vendor?.name_ar || note.vendor?.name) : (note.vendor?.name_en || note.vendor?.name_ar || note.vendor?.name)) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.project_name || (isRTL ? (note.project?.name_ar || note.project?.name) : (note.project?.name_en || note.project?.name_ar || note.project?.name)) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.note_number || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(note.amount) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.issue_date) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.due_date) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{getStatusText(note.status) || '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px'
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// All Notes Payable Preview Template Component
const AllNotesPayablePreviewTemplate = ({ notesPayable, pagination, isRTL, t, formatDate, formatCurrency, getStatusText, publicUrl, headerLogo, chunkIndex = 0, startIndex = 0, totalChunks = 1 }) => {
  if (!notesPayable || notesPayable.length === 0) return null;
  const isFirstPage = chunkIndex === 0;
  return (
    <div
      id={`preview-notes-payable-pdf-all-${chunkIndex}`}
      style={{
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        margin: '0 auto',
        overflow: 'hidden',
        marginBottom: chunkIndex < totalChunks - 1 ? '20px' : 0
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        height: '1043px',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          border: '2px solid #000',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%', flex: 1 }}>
            {isFirstPage && (
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '95%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0,
                  display: 'block',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}
                crossOrigin="anonymous"
              />
            )}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <h1 style={{ textAlign: "center", fontFamily: '"Times New Roman", "Times", serif', fontSize: '18px' }}>{t('notesPayable.title')}</h1>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>#</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.vendor')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.noteNumber')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.amount')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.issueDate')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.dueDate')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesPayable.tableHeaders.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notesPayable.map((note, index) => (
                      <tr key={note.id || index} style={{ backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{startIndex + index + 1}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{note.vendor_name || (isRTL ? (note.vendor?.name_ar || note.vendor?.name) : (note.vendor?.name_en || note.vendor?.name_ar || note.vendor?.name)) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{convertArabicNumbersToEnglish(note.note_number) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrency(note.amount) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDate(note.issue_date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDate(note.due_date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{getStatusText(note.status) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px'
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesPayable;

